const DEFAULT_LOCAL_DEV_HOST_PATTERNS = ['localhost', '127.0.0.1', '::1'];

const normalizeHost = (value?: string): string | undefined => {
  if (!value) {
    return undefined;
  }

  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return undefined;
  }

  const candidates = trimmedValue.includes('://') ? [trimmedValue] : [trimmedValue, `http://${trimmedValue}`];

  for (const candidate of candidates) {
    try {
      const hostname = new URL(candidate).hostname.trim().toLowerCase();
      if (hostname) {
        return hostname;
      }
    } catch {
      // Try the next candidate.
    }
  }

  return trimmedValue.toLowerCase();
};

const matchesHostPattern = (hostname: string, pattern: string): boolean => {
  if (pattern.startsWith('*.')) {
    const suffix = pattern.slice(2);
    return hostname === suffix || hostname.endsWith(`.${suffix}`);
  }

  return hostname === pattern;
};

const normalizePatterns = (patterns?: string[]): string[] => {
  if (!patterns?.length) {
    return [];
  }

  const normalizedPatterns = patterns
    .map((pattern) => pattern.trim())
    .filter(Boolean)
    .map((pattern) => {
      if (pattern.startsWith('*.')) {
        const normalizedWildcardHost = normalizeHost(pattern.slice(2));
        return normalizedWildcardHost ? `*.${normalizedWildcardHost}` : undefined;
      }

      return normalizeHost(pattern);
    })
    .filter((pattern): pattern is string => Boolean(pattern));

  return Array.from(new Set(normalizedPatterns));
};

export const isLocalDevHost = (hostname?: string, customLocalDevHostPatterns?: string[]): boolean => {
  const normalizedHost = normalizeHost(hostname);

  if (!normalizedHost) {
    return false;
  }

  const normalizedPatterns = normalizePatterns([...DEFAULT_LOCAL_DEV_HOST_PATTERNS, ...(customLocalDevHostPatterns ?? [])]);

  return normalizedPatterns.some((pattern) => matchesHostPattern(normalizedHost, pattern));
};
